import React, { useEffect } from 'react'
import {
    Box,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import type { ApplicationWorkbookTemplateListItem } from './workbook.types'

export type WorkbookConfigStartMode = 'template' | 'blank'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    templateOption: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        whiteSpace: 'normal',
    },
    templateDescription: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        lineHeight: 1.3,
    },
    templateSelectWrap: {
        position: 'relative',
    },
    loadingOverlay: {
        position: 'absolute',
        right: theme.spacing(1),
        top: '50%',
        transform: 'translateY(-50%)',
    },
}))

interface WorkbookConfigCreateFieldsProps {
    name: string
    onNameChange: (value: string) => void
    startMode: WorkbookConfigStartMode
    onStartModeChange: (mode: WorkbookConfigStartMode) => void
    templateKey: string
    onTemplateKeyChange: (key: string) => void
    templates: ApplicationWorkbookTemplateListItem[]
    templatesLoading?: boolean
    nameAutoFocus?: boolean
}

export const WorkbookConfigCreateFields: React.FC<WorkbookConfigCreateFieldsProps> = ({
    name,
    onNameChange,
    startMode,
    onStartModeChange,
    templateKey,
    onTemplateKeyChange,
    templates,
    templatesLoading = false,
    nameAutoFocus = true,
}) => {
    const classes = useStyles()

    useEffect(() => {
        if (startMode !== 'template' || templates.length === 0) {
            return
        }
        if (
            !templateKey ||
            !templates.some((template) => template.key === templateKey)
        ) {
            onTemplateKeyChange(templates[0].key)
        }
    }, [startMode, templateKey, templates, onTemplateKeyChange])

    return (
        <Box className={classes.root}>
            <TextField
                autoFocus={nameAutoFocus}
                fullWidth
                variant="outlined"
                size="small"
                label="Workbook name"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="e.g. Standard Student"
            />

            <FormControl component="fieldset">
                <RadioGroup
                    value={startMode}
                    onChange={(e) =>
                        onStartModeChange(
                            e.target.value as WorkbookConfigStartMode,
                        )
                    }
                >
                    <FormControlLabel
                        value="template"
                        control={<Radio color="primary" size="small" />}
                        label="Start from template"
                    />
                    <FormControlLabel
                        value="blank"
                        control={<Radio color="primary" size="small" />}
                        label="Start blank"
                    />
                </RadioGroup>
                {startMode === 'blank' && (
                    <FormHelperText>
                        Not recommended — creates a workbook with default
                        platform buckets only.
                    </FormHelperText>
                )}
            </FormControl>

            {startMode === 'template' && (
                <FormControl
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={templatesLoading || templates.length === 0}
                    className={classes.templateSelectWrap}
                >
                    <InputLabel id="workbook-template-select-label">
                        Template
                    </InputLabel>
                    <Select
                        labelId="workbook-template-select-label"
                        label="Template"
                        value={templateKey}
                        onChange={(e) =>
                            onTemplateKeyChange(String(e.target.value))
                        }
                    >
                        {templates.map((template) => (
                            <MenuItem key={template.key} value={template.key}>
                                <Box className={classes.templateOption}>
                                    <Typography variant="body2">
                                        {template.name}
                                    </Typography>
                                    {template.description ? (
                                        <Typography
                                            className={
                                                classes.templateDescription
                                            }
                                        >
                                            {template.description}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                    {templatesLoading && (
                        <CircularProgress
                            size={18}
                            className={classes.loadingOverlay}
                        />
                    )}
                </FormControl>
            )}
        </Box>
    )
}
